/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { useTranslate } from "@adapptio/lib-components";
import {
	isModel,
	isSchemaConstMapModel,
	isSchemaConstMapModelEntry,
	isSchemaFlowNodeListModel,
	isSchemaFlowNodeModel,
	TGenericModelNode,
	TGetBlueprintSchemaModel
} from "@adapptio/lib-blueprint";
import { isFunction } from "@adapptio/lib-shared";
import { InspectMapEntry } from "../../../ui/components/Inspector/InspectMapEntry";
import { InspectSingleNode } from "../../../ui/components/Inspector/InspectSingleNode";
import {
	isSelectionItemActionFlowNode,
	isSelectionItemActionParameter,
	TActionEditSelectionItem
} from "./ActionEditSelection";
import { InspectFlowNode } from "../../../ui/components/Inspector/InspectFlowNode";
import { TBlueprintActionNodeTypes } from "@adapptio/lib-core";
import { ActionEditorView } from "../ActionEditorEdit/ActionEditorView";

export interface IInspectActionSelectionItemProps {
	selectionItem: TActionEditSelectionItem;
	getNodeById: (nodeId: number) => TGenericModelNode;
	view: ActionEditorView;
}

const pathParameter = [ "$param" ];
const pathFlowNode = [ "$flowNode" ];
const pathNode = [ "$node" ];

export const InspectActionSelectionItem: React.FC<IInspectActionSelectionItemProps> = ({
	selectionItem,
	getNodeById,
	view
}) => {
	const t = useTranslate();

	const getModelNodeById = useCallback<IInspectActionSelectionItemProps["getNodeById"]>(
		(nodeId) => {
			if (!isFunction(getNodeById)) {
				console.error("Property 'getNodeById' is not a function.");
				return null;
			}

			return getNodeById(nodeId);
		},
		[ getNodeById ]
	);

	// Return proper inspector for selected item
	if (isSelectionItemActionParameter(selectionItem)) {
		const parentModel = getModelNodeById(selectionItem.parentMapModelId);
		const modelEntry = selectionItem.mapEntry;

		if (!isSchemaConstMapModel(parentModel) || !isSchemaConstMapModelEntry(modelEntry)) {
			return null;
		}

		return (
			<InspectMapEntry
				model={parentModel}
				modelEntry={modelEntry}
				path={pathParameter}
				depth={0}
				title={t("editor", "Parameter")}
			/>
		);
	}

	if (isSelectionItemActionFlowNode(selectionItem)) {
		const nodeList = getModelNodeById(selectionItem?.nodeListModelId);
		const node = getModelNodeById(selectionItem?.nodeId);

		if (isSchemaFlowNodeModel(node) && isSchemaFlowNodeListModel(nodeList)) {
			let overrideLabel: string;
			let overrideIcon: string;

			if (node.type === "integration") {
				const opts = node.opts.template as TGetBlueprintSchemaModel<
					TBlueprintActionNodeTypes["integration"]["opts"]
				>;
				const integrationType = view.getIntegrationType(opts.integrationType);
				const integrationFn = view.getIntegrationFunction(opts.integrationType, opts.functionName);

				const integrationLabel = t("editor", integrationType?.label);
				const fnLabel = t("editor", integrationFn?.label);

				overrideLabel = `${integrationLabel} / ${fnLabel}`;
				overrideIcon = integrationFn.icon || "mdi/help-circle";
			}

			return (
				<InspectFlowNode
					model={node}
					nodeList={nodeList}
					depth={0}
					path={pathFlowNode}
					overrideLabel={overrideLabel}
					overrideIcon={overrideIcon}
				/>
			);
		}
	}

	const selectedModel = getModelNodeById(selectionItem?.nodeId);

	if (isModel(selectedModel)) {
		return <InspectSingleNode model={selectedModel} depth={0} path={pathNode} />;
	}

	return null;
};
